import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import { ColumnsType } from 'antd/lib/table';

import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import { BaseTable } from 'components/modules/BaseTable';
import { userManagementTabFlex } from 'flex/view/UsersManagement/UserManagementTab';

import { TabKeyEnum } from '../..';
import { createColumns } from './createColumns';
import MfaSearch from './MfaSearch';
import MfaToolbar from './MfaToolbar';
import MfaBulkDisableModal from '../Modals/MfaBulkDisableModal';
import MfaPermissionModal from '../Modals/MfaPermissionModal';

const PULLDOWN_LENGTH = 1000;
// 多要素認証解除後に一覧を再検索するまでの待ち時間(ms)。
// 解除(UPDATE)はマスタ、直後の一覧検索(SELECT)はレプリカに向くため、
// レプリカへ反映される前に検索すると解除前の状態が返る。少し待ってから再検索する。
const MFA_DISABLE_REFETCH_DELAY_MS = 1000;

interface Props {
    tabActiveKey: (typeof TabKeyEnum)[keyof typeof TabKeyEnum];
    hasTouchScreen: boolean;
}

/***
 * MFA管理 ユーザーリスト一覧用component
 */
const MfaList: React.FC<Props> = (props) => {
    const [searchForm] = Form.useForm<User.Search>();
    const tabSelector = useAppSelector((state) => state.userManagementTab);
    const globalSelector = useAppSelector((state) => state.globalState);
    const isControllableUser = useAppSelector(
        (state) => state.isControllableUser.payload,
    );
    const { usersLoading } = useAppSelector(
        userManagementTabFlex.selectors.loading,
    );
    // 多要素認証解除connection セレクタ
    const usersDisableTwoFactorAuthConnection = useAppSelector(
        (state) => state.usersDisableTwoFactorAuthConnection,
    );
    const dispatch = useDispatch();

    const isFirstRender = useRef(true);
    const prevDisableFetch = useRef(false);
    const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isOpenMfaBulkDisableModal, setIsOpenMfaBulkDisableModal] =
        useState(false);
    const [isOpenMfaPermissionModal, setIsOpenMfaPermissionModal] =
        useState(false);

    useEffect(() => {
        if (globalSelector.currentTerm) {
            dispatch(Actions.view.viewLogic.userManagementTab.openView());
        }
    }, [globalSelector.currentTerm, dispatch]);

    useEffect(() => {
        if (props.tabActiveKey !== 'management') return;

        // 余計な通信が発生しないように初回表示時はスキップ
        if (isFirstRender.current) return;

        const search = tabSelector.search;
        const current = tabSelector.pagination.current;
        dispatch(
            Actions.view.viewLogic.userManagementTab.search(search, current, 'mfa'),
        );

        const organizationUuid = searchForm.getFieldValue('organization_uuid');

        const roleSearchBody = organizationUuid
            ? {
                  page_size: PULLDOWN_LENGTH,
                  organization_uuid: organizationUuid,
              }
            : { page_size: PULLDOWN_LENGTH };

        dispatch(Actions.http.connection.role.search(roleSearchBody));

        const classesSearchBody = organizationUuid
            ? {
                  page_size: PULLDOWN_LENGTH,
                  organization_uuid: organizationUuid,
                  term_uuid: globalSelector.currentTerm,
              }
            : {
                  page_size: PULLDOWN_LENGTH,
                  term_uuid: globalSelector.currentTerm,
              };

        dispatch(
            Actions.http.connection.classes.searchForListMap(
                classesSearchBody,
                undefined,
                globalSelector.currentTerm,
            ),
        );
    }, [props.tabActiveKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            dispatch(Actions.view.viewLogic.userManagementTab.closeView());
        };
    }, [dispatch]);

    useEffect(() => {
        isFirstRender.current = false;
        setIsOpenMfaBulkDisableModal(false);
        setIsOpenMfaPermissionModal(false);
    }, []);

    // 多要素認証設定の解除（個別・一括）成功時、一覧を再検索して
    // 「多要素認証の状態」列を再読み込みなしで最新化する。
    // リクエスト中(fetch=true) → 完了(fetch=false) への遷移時のみ実行し、
    // ストアに残った成功ステータスによる再表示時の誤検索を防ぐ。
    // 再検索はレプリカラグを考慮して MFA_DISABLE_REFETCH_DELAY_MS 待ってから行う。
    useEffect(() => {
        const { fetch, status } = usersDisableTwoFactorAuthConnection.meta;
        if (
            prevDisableFetch.current &&
            !fetch &&
            status === Actions.statusEnum.SUCCESS
        ) {
            // 解除済みの行は選択不可となるため、選択状態を解除しておく
            dispatch(Actions.view.viewLogic.userManagementTab.selectRows([]));
            if (refetchTimerRef.current) {
                clearTimeout(refetchTimerRef.current);
            }
            refetchTimerRef.current = setTimeout(() => {
                refetchTimerRef.current = null;
                dispatch(
                    Actions.view.viewLogic.userManagementTab.search(
                        tabSelector.search,
                        tabSelector.pagination.current,
                        'mfa',
                    ),
                );
            }, MFA_DISABLE_REFETCH_DELAY_MS);
        }
        prevDisableFetch.current = fetch;
    }, [usersDisableTwoFactorAuthConnection]); // eslint-disable-line react-hooks/exhaustive-deps

    // アンマウント時は待機中の再検索を取り消す
    useEffect(() => {
        return () => {
            if (refetchTimerRef.current) {
                clearTimeout(refetchTimerRef.current);
                refetchTimerRef.current = null;
            }
        };
    }, []);

    // 検索結果をCSVでダウンロードさせる
    const handleCsvDownload = (
        encoding: Encoding,
        options?: Record<string, unknown>,
    ) => {
        dispatch(
            Actions.view.viewLogic.userManagementTab.downloadCsv(
                encoding,
                options?.isSimpleExport,
                'mfa',
            ),
        );
    };

    // 多要素認証設定の解除（個別）
    const handleMfaReset = (record: User) => {
        dispatch(
            Actions.http.connection.users.disableTwoFactorAuth({
                user_uuids: [record.uuid],
            }),
        );
    };

    const handleOpenMfaBulkDisableModal = () => setIsOpenMfaBulkDisableModal(true);

    // 多要素認証設定の解除（一括）
    const handleMfaDisableBulk = () => {
        const userUuids = (tabSelector.selectedRows ?? [])
            .concat(tabSelector.currentSelectedRows)
            .map((e) => e.uuid);
        dispatch(
            Actions.http.connection.users.disableTwoFactorAuth({
                user_uuids: userUuids,
            }),
        );
        setIsOpenMfaBulkDisableModal(false);
    };

    const handleOpenMFAPermissionModal = (record: User) => {
        setIsOpenMfaPermissionModal(true);
        dispatch(
            Actions.view.viewLogic.userManagementTab.selectRow(
                record,
            ),
        );
    };

    const handleMfaPermission = (values: { enable: boolean }) => {
        dispatch(Actions.http.connection.users.changeMfaEnable(values, tabSelector.selectedRow?.uuid ?? '', ));
        setIsOpenMfaPermissionModal(false);
    };

    const columns: ColumnsType<User> = createColumns(
        props.hasTouchScreen,
        handleMfaReset,
        handleOpenMFAPermissionModal,
        isControllableUser,
        tabSelector,
        globalSelector,
    );

    const rowSelection: TableRowSelection<User> = {
        selectedRowKeys: tabSelector.currentSelectedRows.map((row) => row.uuid),
        onChange: (selectedRowKeys, selectedRows) => {
            dispatch(
                Actions.view.viewLogic.userManagementTab.selectRows(
                    selectedRows,
                ),
            );
        },
        getCheckboxProps: (record) => ({
            // MFA設定済みのユーザのみ一括解除の対象として選択可能
            disabled: !record.registered_mfa,
            name: record.login_id,
        }),
    };

    return (
        <div className='users-management users-list container'>
            <MfaSearch form={searchForm} />

            <MfaToolbar
                tabSelector={tabSelector}
                handleCsvDownload={handleCsvDownload}
                handleOpenMfaBulkDisableModal={handleOpenMfaBulkDisableModal}
            />

            <BaseTable
                rowSelection={rowSelection}
                emptyDescription={
                    <>
                        条件に一致する利用者はいませんでした。 <br />
                        検索条件を変更して再度検索をしてください。
                    </>
                }
                loading={usersLoading}
                columns={columns}
                dataSource={tabSelector.usersList || []}
                scroll={{ x: 1950 }}
                sticky
                pagination={{
                    ...tabSelector.pagination,
                    showTotal: (total, range) => {
                        return `全${total}件中 ${range[0]}-${range[1]} 件`;
                    },
                    onChange: (page, page_size) => {
                        dispatch(
                            Actions.view.viewLogic.userManagementTab.page(
                                page,
                                page_size ?? 50,
                            ),
                        );
                    },
                }}
            />

            <MfaBulkDisableModal
                isOpen={isOpenMfaBulkDisableModal}
                onCancel={() => setIsOpenMfaBulkDisableModal(false)}
                onSubmit={handleMfaDisableBulk}
            />

            <MfaPermissionModal
                isOpen={isOpenMfaPermissionModal}
                onCancel={() => setIsOpenMfaPermissionModal(false)}
                onSubmit={handleMfaPermission}
            />
        </div>
    );
};

export default MfaList;
